/**
 * Manager Access Request Handler
 * 
 * Automatically checks for pending manager access requests
 * and shows the popup to company owners
 */

import React, { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import ManagerAccessRequestPopup from './ManagerAccessRequestPopup';

interface ManagerAccessRequestHandlerProps {
  userId: string;
  isEntrepreneur: boolean; // Only show for entrepreneurs
}

export const ManagerAccessRequestHandler: React.FC<ManagerAccessRequestHandlerProps> = ({
  userId,
  isEntrepreneur
}) => {
  const [hasPendingRequests, setHasPendingRequests] = useState(false);
  const [showPopup, setShowPopup] = useState(false);

  useEffect(() => {
    if (!userId || !isEntrepreneur) {
      return;
    }

    // Listen for pending manager access requests
    const requestsRef = collection(db, 'managerAccessRequests');
    const q = query(
      requestsRef,
      where('companyOwnerId', '==', userId),
      where('status', '==', 'pending')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const pendingCount = snapshot.size;
      setHasPendingRequests(pendingCount > 0);

      // Automatically show popup when there are new requests
      if (pendingCount > 0 && !showPopup) {
        // Small delay to avoid showing immediately on page load
        setTimeout(() => {
          setShowPopup(true);
        }, 2000);
      }
    });

    return () => unsubscribe();
  }, [userId, isEntrepreneur]);

  if (!hasPendingRequests || !showPopup) {
    return null;
  }

  return (
    <ManagerAccessRequestPopup
      userId={userId}
      onClose={() => setShowPopup(false)}
    />
  );
};

export default ManagerAccessRequestHandler;
